import { useId, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { HospitalQueue } from '@dru/types';
import { Button, Input, cn } from '@dru/ui';
import { ScanLine } from 'lucide-react';
import { api } from '../../../lib/api';
import { errorMessage } from '../../../lib/auth';

/** A scanned appointment pass is a long random token; an employee code is short. */
export const PASS_MIN_LENGTH = 20;
export const looksLikePass = (v: string) => v.trim().length >= PASS_MIN_LENGTH && !/\s/.test(v.trim());

const QUEUE_LIMIT = 200;

type Outcome =
  | { kind: 'idle' }
  | { kind: 'busy' }
  | { kind: 'closed'; name: string; code: string; encounterId: string }
  | { kind: 'waiting'; name: string; code: string }
  | { kind: 'none'; code: string; capped: boolean }
  | { kind: 'error'; message: string };

/**
 * "Scan the employee, land on their visit."
 *
 * The printed ID card's QR holds the employee code and nothing else; the
 * appointment pass holds a one-time token. Either can be scanned (a scanner
 * types into the focused field and presses Enter) or the code typed:
 *  - checked in and on the floor → their encounter, the same one every station uses;
 *  - booked, not checked in      → the check-in desk with the booking chosen
 *                                  (identity is still confirmed there);
 *  - a pass token                → the check-in desk, which verifies the pass;
 *  - closed today                → said so, with a link to view it.
 * Matched on the exact employee code only: a name or phone number never
 * opens anybody's visit.
 */
export function ScanLookup({ partnerId, prefix, canCheckIn, className, compact }: {
  partnerId: string;
  prefix: string;
  canCheckIn: boolean;
  className?: string;
  compact?: boolean;
}) {
  const nav = useNavigate();
  const [value, setValue] = useState('');
  const [out, setOut] = useState<Outcome>({ kind: 'idle' });
  const inputId = useId();

  async function submit(ev: FormEvent) {
    ev.preventDefault();
    const raw = value.trim();
    if (!raw) return;
    if (looksLikePass(raw)) {
      if (canCheckIn) { nav(`${prefix}/check-in?pass=${encodeURIComponent(raw)}`); return; }
      setOut({ kind: 'error', message: 'That is an appointment pass. The front desk checks it in; then scan the ID card here.' });
      return;
    }
    const code = raw.toUpperCase();
    setOut({ kind: 'busy' });
    try {
      const q = await api.get<HospitalQueue>(`/encounters/queue?partnerId=${partnerId}&limit=${QUEUE_LIMIT}&days=1`);
      const same = (c: string) => c.toUpperCase() === code;
      const floor = q.inProgress.find((e) => same(e.employee.employeeCode));
      if (floor) { setValue(''); setOut({ kind: 'idle' }); nav(`${prefix}/encounter/${floor.encounterId}`); return; }
      const booked = q.awaitingCheckIn.find((a) => same(a.employee.employeeCode));
      if (booked) {
        if (canCheckIn) {
          setValue(''); setOut({ kind: 'idle' });
          nav(`${prefix}/check-in?appointment=${booked.appointmentId}&code=${encodeURIComponent(booked.employee.employeeCode)}`);
          return;
        }
        setOut({ kind: 'waiting', name: booked.employee.fullName, code: booked.employee.employeeCode });
        return;
      }
      const done = (q.completed ?? []).find((c) => same(c.employee.employeeCode));
      if (done) { setOut({ kind: 'closed', name: done.employee.fullName, code: done.employee.employeeCode, encounterId: done.encounterId }); return; }
      const capped = q.awaitingCheckIn.length >= QUEUE_LIMIT || q.inProgress.length >= QUEUE_LIMIT;
      setOut({ kind: 'none', code, capped });
    } catch (e) {
      setOut({ kind: 'error', message: errorMessage(e) });
    } finally {
      (document.getElementById(inputId) as HTMLInputElement | null)?.select();
    }
  }

  return (
    <form onSubmit={submit} className={cn('rounded-card border border-primary-200 bg-primary-50 p-3', className)} aria-label="Find an employee by scanning">
      <div className="flex flex-wrap items-end gap-2">
        <label className="min-w-0 flex-[1_1_16rem]">
          <span className={cn('flex items-center gap-1.5 font-medium text-neutral-900', compact ? 'text-small' : 'text-body')}>
            <ScanLine className="h-4 w-4 text-primary-600" aria-hidden />
            Scan the employee&rsquo;s ID card or pass
          </span>
          {!compact && (
            <span className="block text-caption text-neutral-600">
              Or type the employee code. Opens their visit if they are checked in, or the check-in desk if they are only booked.
            </span>
          )}
          <Input
            id={inputId}
            value={value}
            onChange={(e) => { setValue(e.target.value); if (out.kind !== 'busy') setOut({ kind: 'idle' }); }}
            spellCheck={false}
            autoComplete="off"
            inputMode="text"
            placeholder="Scan, or type e.g. DEMO-0004"
            aria-label="Employee code or pass"
            className="mt-1 font-mono"
          />
        </label>
        <Button type="submit" loading={out.kind === 'busy'} disabled={!value.trim()}>Open</Button>
      </div>
      <p className={cn('text-caption', out.kind !== 'idle' && out.kind !== 'busy' && 'mt-1.5')} aria-live="polite">
        {out.kind === 'waiting' && (
          <span className="text-dueSoon-600">{out.name} ({out.code}) is booked but not checked in yet. Send them to the front desk first.</span>
        )}
        {out.kind === 'closed' && (
          <span className="text-neutral-700">
            {out.name} ({out.code})&rsquo;s visit is already closed.{' '}
            <Link to={`${prefix}/encounter/${out.encounterId}`} className="font-medium text-primary-700 hover:underline">View it</Link>
          </span>
        )}
        {out.kind === 'none' && (
          <span className="text-overdue-600">
            Nobody with code {out.code} is booked or on the floor here from yesterday to tomorrow.
            {out.capped && ' The list was too long to search completely; use Check in to find them by name.'}
          </span>
        )}
        {out.kind === 'error' && <span className="text-overdue-600">{out.message}</span>}
      </p>
    </form>
  );
}
